import React, { useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Dropdown } from 'react-bootstrap'
import { Link, NavLink } from 'react-router-dom'
import classNames from 'classnames'
import { IntegrationIcon } from '../../../components/IntegrationIcon'
import { getIntegrationDisplayName, type IntegrationServiceId } from '../../../common/integrations'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetMeQuery } from '../../../store/user/user'
import { Button, type ButtonVariant } from '../../../components/Button/Button'
import styles from './CrossClientActionsDropdown.module.scss'

// react-bootstrap's Dropdown.Menu passes `show` / `close` as props on the underlying
// element. Strip them so React doesn't warn about non-DOM attributes on the portaled div.
type PortalDropdownMenuProps = React.HTMLAttributes<HTMLDivElement> & {
  show?: boolean
  close?: () => void
}

const PortalDropdownMenu = React.forwardRef<HTMLDivElement, PortalDropdownMenuProps>(({ show, close, children, ...rest }, ref) => {
  void close

  const menu = (
    <div ref={ref} {...rest}>
      {show ? children : null}
    </div>
  )

  return typeof document === 'undefined' ? menu : createPortal(menu, document.body)
})
PortalDropdownMenu.displayName = 'PortalDropdownMenu'

export interface CrmAction {
  label: string
  icon: string
  onClick: () => void
}

export type CrmActionsMap = Partial<Record<IntegrationServiceId, CrmAction[]>>

interface ActionsDropdownItemProps {
  icon?: string
  iconNode?: React.ReactNode
  label: string
  onClick?: () => void
  to?: string
  disabled?: boolean
}

const ITEM_CLASS_NAME = 'paragraph-medium d-flex align-items-center gap-2 ps-0 py-2'

export const ActionsDropdownItem: React.FC<ActionsDropdownItemProps> = ({ icon, iconNode, label, onClick, to, disabled }) => {
  const linkProps = to ? { as: Link as React.ElementType, to, target: '_blank', rel: 'noreferrer noopener' } : {}
  return (
    <Dropdown.Item onClick={onClick} disabled={disabled} className={ITEM_CLASS_NAME} {...linkProps}>
      {iconNode ? iconNode : <i className={classNames('bi', icon)} />}
      <span>{label}</span>
    </Dropdown.Item>
  )
}

interface ActionsToggleProps {
  variant?: ButtonVariant
  className?: string
  disabled?: boolean
  'aria-label'?: string
  children?: React.ReactNode
}

// `bsPrefix` strips react-bootstrap's default `.dropdown-toggle` class, which would
// otherwise stack on top of the wrapped Button's variant styling.
export const ActionsToggle: React.FC<ActionsToggleProps> = ({ variant, className, disabled, 'aria-label': ariaLabel, children }) => (
  <Dropdown.Toggle as={Button} bsPrefix='actions-dropdown-toggle' variant={variant} className={className} disabled={disabled} aria-label={ariaLabel}>
    {children}
  </Dropdown.Toggle>
)

interface CrossClientActionsDropdownProps {
  toggle: React.ReactNode
  count: number
  singleClientName?: string
  clientId?: string
  clientIntegrationServiceIds?: IntegrationServiceId[]
  crmActions: CrmActionsMap
  children?: React.ReactNode
}

export const CrossClientActionsDropdown: React.FC<CrossClientActionsDropdownProps> = ({
  toggle,
  count,
  singleClientName,
  clientId,
  clientIntegrationServiceIds,
  crmActions,
  children,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: currentUser } = useGetMeQuery({ accountId })

  const connectedCrmServiceIds = useMemo(
    () => Array.from(new Set((currentUser?.integrations ?? []).map((i) => i.serviceId))).filter((id) => (crmActions[id]?.length ?? 0) > 0),
    [currentUser?.integrations, crmActions],
  )

  const targetLabel = count === 1 && singleClientName ? singleClientName : `${count} ${count === 1 ? 'client' : 'clients'} in the list`

  return (
    <Dropdown align='start'>
      {toggle}
      <Dropdown.Menu as={PortalDropdownMenu} popperConfig={{ strategy: 'fixed' }} className={classNames(styles.menu, 'p-2 py-3')}>
        <div className='paragraph-medium zocks-px-2 zocks-py-1 pb-2'>
          Pick action for <span className='fw-bold'>{targetLabel}</span>
        </div>
        <div className={styles.scrollableContent}>
          {children}
          {connectedCrmServiceIds.map((serviceId) => {
            const integrationName = getIntegrationDisplayName(serviceId)
            const clientHasIntegration = !clientIntegrationServiceIds || clientIntegrationServiceIds.includes(serviceId)
            return (
              <React.Fragment key={serviceId}>
                <Dropdown.Divider />
                <div className='d-flex align-items-center gap-2 zocks-px-2 zocks-py-1 paragraph-small fw-bold text-uppercase zocks-text-gray-900 py-2'>
                  <IntegrationIcon serviceId={serviceId} width={'1rem'} height={'1rem'} />
                  <span>{integrationName}</span>
                </div>
                {clientHasIntegration ? (
                  crmActions[serviceId]?.map((action) => (
                    <ActionsDropdownItem key={action.label} icon={action.icon} label={action.label} onClick={action.onClick} />
                  ))
                ) : (
                  <div className='zocks-px-2 zocks-py-1 paragraph-small zocks-text-gray-600 ps-4 pe-4 py-1'>
                    <span>
                      This client isn't synced with {integrationName}. Add their {integrationName} ID in their{' '}
                    </span>
                    <NavLink
                      className='d-inline-flex text-decoration-none zocks-text-primary'
                      to={`/library/manage-contacts/contacts/${clientId}`}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      <span>profile</span>
                    </NavLink>
                    <span> to use these actions.</span>
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  )
}
